import { animate, state, style, transition, trigger } from '@angular/animations';
import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, TemplateRef, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { BehaviorSubject, Observable, Subscription, debounceTime, distinctUntilChanged, finalize, of, switchMap } from 'rxjs';
import { PagedResult } from '../../../shared/models/paged-result';
import { SortItem } from '../../../shared/models/sortItem';
import { StorageService } from '../../../shared/services/storage.service';
import { AegisGridOptionsDialogComponent, options } from '../aegis-grid-options-dialog/aegis-grid-options-dialog.component';
import { FormControl } from '@angular/forms';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';

export interface SearchValue {
  displayName: string;
  value: string;
}
export interface SaveRowEvent<T> {
  element: T;
  complete: (success: boolean) => void;
}

export interface SaveRowsEvent<T> {
  elements: T[];
  complete: (success: boolean) => void;
}

export class filterSettings {
  Vertical?: boolean;
  Values?: any[];
  Multiselect?: boolean;
  InputType?: string;
  Range?: boolean;

  static dateColumnVertical(): filterSettings {
    return {
      Vertical: true
    };
  }
}

export class columnOption {
  order: number;
  hidden: boolean;
  removed: boolean;
}

export class columnSetting {
  Width?: string;
  MinWidth?: string;
  MaxWidth?: string;
  PreFix?: string;
  HasFooter?: boolean;
  FooterText?: string;
  FooterAction?: string;
  FooterCurrency?: boolean;
  RoundTo?: number;
  AggregateName?: string;
  DefaultHidden?: boolean;
  Format?: string;
  FrontFixed?: boolean;
  Fixed?: boolean;
  Clickable?: boolean;
  CanClick?: (row: any) => boolean;
  Editable?: boolean;
  NotSortable?: boolean;
  Currency?: boolean;
  CurrencyAllowNegative?: boolean;
  Search?: boolean;
  SearchFn?: (term: string) => Observable<SearchValue[]>;

  static standardColumn(hidden?: boolean): columnSetting {
    return {
      Width: '90px',
      DefaultHidden: hidden
    };
  }

  static doOverrides(base: columnSetting, override: columnSetting) {
    return { ...base, ...override };
  }

  static standardColumnWithOverrides(overrides: columnSetting): columnSetting {
    return { ...this.standardColumn(), ...overrides };
  }

  static dateColumn(hidden?: boolean, format?: string): columnSetting {
    return {
      Width: '120px',
      DefaultHidden: hidden,
      Format: format
    };
  }

  static textColumn(hidden?: boolean): columnSetting {
    return {
      Width: '150px',
      DefaultHidden: hidden
    };
  }

  static longTextColumn(hidden?: boolean): columnSetting {
    return {
      Width: '300px',
      DefaultHidden: hidden
    };
  }

  static currencyColumn(hidden?: boolean): columnSetting {
    return {
      Width: '90px',
      Currency: true,
      DefaultHidden: hidden
    };
  }
}

export class calculateSettings {
  Always: boolean;
}

export class columnDef {
  Name: string;
  Display: string;
  DisplayName?: string;
  Type: "string" | "date" | "number" | "bool" | "dateTime" | "percent";
  Mask?: string;
  IncludeFilter: boolean;
  FilterSettings?: filterSettings
  ColumnSettings?: columnSetting;
  Remove?: boolean;
  IsCalculated?: boolean;
  CalculateOptions?: calculateSettings;
  Calculation?: (param1: any) => any;
}

@Component({
  selector: 'app-aegis-grid',
  templateUrl: './aegis-grid.component.html',
  styleUrls: ['./aegis-grid.component.scss'],
  animations: [
    trigger('detailExpand', [
      state('collapsed', style({ height: '0px', minHeight: '0' })),
      state('expanded', style({ height: '*' })),
      transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)'))
    ]),
    trigger('activeViewStateChild', [
      state('inactive', style({
        display: 'none'
      })),
      state('active', style({
        display: 'block'
      })),
      transition('inactive => active', [
        style({ transform: 'translateX(100%)' }),
        animate('200ms ease-in', style({ transform: 'translateX(0%)' }))
      ])
    ]),
    trigger('activeViewStateParent', [
      state('inactive', style({
        display: 'none'
      })),
      state('active', style({
        display: 'block'
      })),
      transition('inactive => active', [
        style({ transform: 'translateX(-100%)' }),
        animate('200ms ease-in', style({ transform: 'translateX(0%)' }))
      ])
    ])
  ]
})
export class AegisGridComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {

  @Input() columns: columnDef[];
  @Input() enableStorage: boolean;
  @Input() storageKeyIdentifier: string;
  @Input() data: BehaviorSubject<PagedResult<any>>;
  private rows: PagedResult<any>;
  @Input() isLoadingResults: boolean;
  @Input() userId: string;
  @Input() getLink: (row: any) => string;
  @Input() minWidth: string;
  @Input() maxWidth: string;
  @Input() pageDisabled: boolean;
  @Input() showFooter: boolean;
  @Input() ignoreStorageExpiration: boolean;
  @Input() canDownload: boolean = false;
  @Input() isRowClickable: (row: any) => boolean;
  @Input() defaultPageSize: number;
  @Input() pageSizeOptions: number[];
  @Input() showPageSize: boolean;
  @Input() rowTemplate: TemplateRef<any>;
  @Input() extraControlsTemplate: TemplateRef<any>;
  @Input() extraControlsTemplateContext: any;
  @Input() cellTemplates: any;
  @Input() cellTemplateContexts: any;
  @Input() canAdd: boolean = false;
  @Input() canEdit: boolean = false;
  @Input() canDelete: boolean = false;
  @Input() useBottomCrudButtons: boolean = false;
  @Input() locked: boolean = false;
  @Input() rowStyle: (row: any) => string;
  @Input() alwaysHideFilterRow: boolean = false;

  @Output() sortItemsChange: EventEmitter<SortItem[]> = new EventEmitter<SortItem[]>();
  @Output() filterChange: EventEmitter<any> = new EventEmitter<any>();
  @Output() pageChange: EventEmitter<PageEvent> = new EventEmitter<PageEvent>();
  @Output() refreshClicked: EventEmitter<any> = new EventEmitter<any>();
  @Output() rowClicked: EventEmitter<any> = new EventEmitter<any>();
  @Output() cellClicked: EventEmitter<any> = new EventEmitter<any>();
  @Output() downloadClicked: EventEmitter<any> = new EventEmitter<any>();
  @Output() addRow: EventEmitter<SaveRowEvent<any>> = new EventEmitter<SaveRowEvent<any>>();
  @Output() saveRow: EventEmitter<SaveRowEvent<any>> = new EventEmitter<SaveRowEvent<any>>();
  @Output() deleteRow: EventEmitter<SaveRowEvent<any>> = new EventEmitter<SaveRowEvent<any>>();
  @Output() deleteRows: EventEmitter<SaveRowsEvent<any>> = new EventEmitter<SaveRowsEvent<any>>();

  @ViewChild('container', { read: ElementRef, static: false }) container: ElementRef;
  @ViewChild('table', { read: ElementRef, static: false }) table: ElementRef;
  @ViewChild('footer', { read: ElementRef, static: false }) footer: ElementRef;

  private lastHeaderSize: number;
  private hideFilterRowDueToSize: boolean = false;
  private temporarilyShowFilterRow: boolean = false;
  private isEditing: boolean = false;
  private isAdding: boolean = false;
  private isDeleting: boolean = false;
  private isAnyEditing: boolean = false;
  private saving: boolean = false;

  private displayedColumns: string[] = [];
  public getDisplayedColumns(): string[] {
    var ary = [];

    if ((this.canAdd || this.canDelete || this.canEdit) && !this.useBottomCrudButtons)
      ary.push('edit');
    else if (this.canEdit && this.useBottomCrudButtons && this.isEditing)
      ary.push('edit');
    else if (this.canDelete && this.useBottomCrudButtons && this.isDeleting)
      ary.push('edit');

    return ary.concat(this.displayedColumns);
  };

  public getDisplayedFilterColumns(): string[] {
    var ary = [];

    if ((this.canAdd || this.canDelete || this.canEdit) && !this.useBottomCrudButtons)
      ary.push('edit-filter');
    else if (this.canEdit && this.useBottomCrudButtons && this.isEditing)
      ary.push('edit-filter');
    else if (this.canDelete && this.useBottomCrudButtons && this.isDeleting)
      ary.push('edit-filter');

    return ary.concat(this.displayedColumns?.map((obj) => obj + '-filter'));
  }

  @ViewChild(MatPaginator, { static: true })
  public paginator: MatPaginator;

  constructor(
    private readonly storageService: StorageService,
    public dialog: MatDialog
  ) {
    this.onFilterChange = new BehaviorSubject<any>({});
    this.onSortChange = new BehaviorSubject<SortItem[]>([]);
    this.onPageChange = new BehaviorSubject<PageEvent>(null);
    this.onColumnOptionsChange = new BehaviorSubject<Record<string, columnOption>>({});

    this.onFilterChange.subscribe(
      (result) => this.filterChange.emit(result)
    );

    this.onSortChange.subscribe(
      (result) => this.sortItemsChange.emit(result)
    );

    this.onPageChange.subscribe(
      (result) => this.pageChange.emit(result)
    );

    this.onColumnOptionsChange.subscribe(
      (result) => {
        if (result && this.columns) {
          //Check for missing column
          let hasMissingColumn: boolean = false;
          //Add missing columns
          for (let i = 0; i < this.columns.length; i++) {
            const col = this.columns[i];

            if (!result[col.Name]) {
              hasMissingColumn = true;
              break;
            }
          }

          //Remove columns that are not in the columns array
          var remove = [];
          for (let prop in result) {
            if (!this.columns.find(col => col.Name === prop)) {
              remove.push(prop);
            }
          }
          for (let i = 0; i < remove.length; i++) {
            delete result[remove[i]];
          }

          if (hasMissingColumn) {
            result = {};

            let visibleIndex = -1;
            let hiddenIndex = -1;
            for (var i = 0; i < this.columns.length; i++) {
              var col = this.columns[i];

              if (col.ColumnSettings?.DefaultHidden == true || col.Remove) {
                hiddenIndex++;
                result[col.Name] = <columnOption>{
                  hidden: true,
                  order: hiddenIndex,
                  removed: col.Remove
                };
              } else {
                visibleIndex++;
                result[col.Name] = <columnOption>{
                  hidden: false,
                  order: visibleIndex,
                  removed: false
                };
              }
            }

            this.storageService.saveData(this.userId, this.getColumnOptionsStorageKey(), null);
            this.onColumnOptionsChange.next(result);
          } else {
            const visibleColumns = Object.entries(result).filter(([prop, data]) => !data.hidden).map(([prop, data]) => ({ prop, order: data.order })).sort((a, b) => a.order - b.order).map((item) => item.prop);

            this.displayedColumns = visibleColumns;
          }
        } else {
          this.displayedColumns = [];
        }
      }
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['userId'] && this.userId) {
      var storedFilter = this.storageService.getData(this.userId, this.getFilterStorageKey());
      var storedSort = this.storageService.getData(this.userId, this.getSortStorageKey());
      var storedPage = this.storageService.getData(this.userId, this.getPageStorageKey());
      var columnOptions = this.storageService.getData(this.userId, this.getColumnOptionsStorageKey());

      if (storedFilter)
        this.onFilterChange?.next(storedFilter);
      else
        this.onFilterChange?.next({});

      if (storedSort)
        this.onSortChange?.next(storedSort);
      else
        this.onSortChange?.next([]);

      if (storedPage) {
        this.paginator.pageIndex = storedPage.pageIndex;

        if (storedPage.pageSize && this.showPageSize)
          this.paginator.pageSize = storedPage.pageSize;
      }

      this.onPageChange?.next({
        length: this.paginator.length,
        pageIndex: this.paginator.pageIndex,
        pageSize: this.paginator.pageSize,
        previousPageIndex: null
      });

      if (columnOptions) {
        this.onColumnOptionsChange.next(columnOptions);
      }
    }

    if (changes['data'] && this.data) {
      this.data.subscribe(result => {
        if (result) {
          if (this.paginator.pageIndex > (result.totalResults / this.paginator.pageSize)) {
            this.paginator.pageIndex = 0;

            this.onPageChange.next({
              length: this.paginator.length,
              pageIndex: this.paginator.pageIndex,
              pageSize: this.paginator.pageSize,
              previousPageIndex: null
            });
          } else if (this.onPageChange && this.paginator.pageIndex != this.onPageChange.value?.pageIndex) {
            this.onPageChange.next({
              length: this.paginator.length,
              pageIndex: this.paginator.pageIndex,
              pageSize: this.paginator.pageSize,
              previousPageIndex: null
            });
          }
        }
      });


    }

    if (changes['columns'] && this.columns) {
      this.onColumnOptionsChange.next(this.onColumnOptionsChange.value);
    }
  }

  private createOutletContext(cellTemplateContexts: any, columnName: string, element: any): any {
    if (cellTemplateContexts && !!cellTemplateContexts[columnName]) {
      var returnValue = { ...cellTemplateContexts[columnName] };

      returnValue.row = element;

      return returnValue;
    } else {
      return { row: element };
    }
  }

  private getLink_Internal(row: any) {
    if (this.getLink)
      return this.getLink(row);

    return null;
  }

  private isSortedDown(col: string): boolean {
    var sortedColumns = this.onSortChange.value;

    for (var i = 0; i < sortedColumns.length; i++) {
      if (sortedColumns[i].column == col) {
        return sortedColumns[i].sortDirection == "desc";
      }
    }

    return false;
  }

  private isSortedUp(col: string): boolean {
    var sortedColumns = this.onSortChange.value;

    for (var i = 0; i < sortedColumns.length; i++) {
      if (sortedColumns[i].column == col) {
        return sortedColumns[i].sortDirection == "asc";
      }
    }

    return false;
  }

  private onSortChange: BehaviorSubject<SortItem[]>;
  private sortClick(canSort: boolean, col: string, event: any) {
    if (!canSort || this.getLocked())
      return;

    if (this.sortItemsChange.observed) {
      var sortedColumns = this.onSortChange.value;

      if (!sortedColumns)
        sortedColumns = [];

      var found = false;
      if (!event.ctrlKey) {
        if (sortedColumns.length > 1 || (sortedColumns.length == 1 && sortedColumns[0].column != col))
          sortedColumns.splice(0, sortedColumns.length);
        else if (sortedColumns.length == 1) {
          found = true;

          if (sortedColumns[0].sortDirection == "desc")
            sortedColumns.pop();
          else
            sortedColumns[0].sortDirection = "desc";
        }
      } else {
        for (var i = 0; i < sortedColumns.length; i++) {
          if (sortedColumns[i].column == col) {
            found = true;

            if (sortedColumns[i].sortDirection == "asc")
              sortedColumns[i].sortDirection = "desc";
            else
              sortedColumns.splice(i, 1);
          }
        }
      }

      if (!found)
        sortedColumns.push({
          column: col,
          sortDirection: "asc"
        });

      this.onSortChange.next(sortedColumns);
      if (this.userId)
        this.storageService.saveData(this.userId, this.getSortStorageKey(), sortedColumns, {
          neverExpires: this.ignoreStorageExpiration
        });
    }
  }

  private onFilterChange: BehaviorSubject<any>;
  public doChangeFilter(event: any, type: string): void {
    var currentFilter = this.onFilterChange.value;

    currentFilter[type] = event.target === undefined ? event.value : event.target.value;

    this.paginator.pageIndex = 0;
    this.onFilterChange.next(currentFilter);
    if (this.userId)
      this.storageService.saveData(this.userId, this.getFilterStorageKey(), currentFilter, {
        neverExpires: this.ignoreStorageExpiration
      });

    this.onFilterChange.next(currentFilter);
  }

  private onColumnOptionsChange: BehaviorSubject<Record<string, columnOption>>;

  private onPageChange: BehaviorSubject<PageEvent>;
  private dataSub: Subscription;
  public ngOnInit() {
    this.paginator.page.subscribe(
      (event) => {
        if (this.userId)
          var val: any = { pageIndex: event.pageIndex };

        if (this.showPageSize) {
          val.pageSize = event.pageSize;
        }

        this.storageService.saveData(this.userId, this.getPageStorageKey(), val, {
          neverExpires: this.ignoreStorageExpiration
        });

        this.onPageChange.next(event);
      }
    );

    this.dataSub = this.data.subscribe((pagedResult) => {
      if (pagedResult?.results) {
        this.generateRowIds(pagedResult.results);
        this.rows = pagedResult;

        //Initialize search controls for each row and column
        this.rows.results.forEach((row, rowIndex) => {
          this.columns.forEach((col) => {
            if (col.ColumnSettings?.Search === true) {
              this.initSearchControl(row.aegisGridRowId, col.Name, col.ColumnSettings.SearchFn);
            }
          });
        });
      }
    })
  }

  generateRowIds(rows: any[]): void {
    rows.forEach((row) => {
      if (!row.aegisGridRowId) {
        row.aegisGridRowId = this.generateUniqueId();
      }
    });
  }

  generateUniqueId(): string {
    return Date.now().toString(36) + Math.random().toString(36).substr(2, 9);
  }

  ngOnDestroy(): void {
    if (this.dataSub) {
      this.dataSub.unsubscribe();
    }
  }

  ngAfterViewInit(): void {
    var ro = new ResizeObserver(entries => {
      for (let entry of entries) {

        //Handle container change
        if (entry.target == this.container.nativeElement) {
          const cr = entry.contentRect;

          var header = this.table.nativeElement.children[0];
          var headerSize = header.offsetHeight;
          var footerSize = this.footer.nativeElement.offsetHeight;

          if (cr.height - (this.hideFilterRowDueToSize ? this.lastHeaderSize : headerSize) < 300) {
            if (!this.hideFilterRowDueToSize) {
              this.lastHeaderSize = headerSize;
              this.hideFilterRowDueToSize = true;
            }
          } else {
            this.hideFilterRowDueToSize = false;
          }
        }
      }
    });

    ro.observe(this.container.nativeElement);
  }

  public doRefresh(): void {
    this.onFilterChange.next(this.onFilterChange.value);
    this.onSortChange.next(this.onSortChange.value);
    this.onPageChange.next(this.onPageChange.value);
    this.refreshClicked.emit();
  }

  public clearFilters(): void {
    this.onFilterChange.next({});
    this.onSortChange.next(<SortItem[]>[]);
    this.paginator.pageIndex = 0;
    this.paginator.pageSize = this.defaultPageSize ?? 10;
    this.onPageChange.next({
      length: this.paginator.length,
      pageIndex: this.paginator.pageIndex,
      pageSize: this.paginator.pageSize,
      previousPageIndex: null
    });

    if (this.userId) {
      this.storageService.saveData(this.userId, this.getFilterStorageKey(), {});
      this.storageService.saveData(this.userId, this.getSortStorageKey(), []);
      this.storageService.saveData(this.userId, this.getPageStorageKey(), {});
    }
  }

  private getFromAction(action: string, col: string, settings: columnSetting) {
    if (!this.data?.value?.results)
      return "";

    if (action == "Sum") {
      var total = this.data.value.results.reduce((accum, curr) => accum + curr[col], 0);

      const roundTo = settings?.RoundTo;
      if (roundTo || roundTo === 0) {
        return total?.toFixed(roundTo);
      } else {
        return total;
      }
    } else if (action == "Aggregate" && this.data.value.aggregates) {
      var total = this.data.value.aggregates[settings.AggregateName];

      const roundTo = settings?.RoundTo;
      if (roundTo || roundTo === 0) {
        return total?.toFixed(roundTo);
      } else {
        return total;
      }
    }
    else {
      return "";
    }
  }

  private openOptions() {
    const dialogRef = this.dialog.open(AegisGridOptionsDialogComponent, {
      panelClass: 'gridOptions',
      width: '800px',
      height: '90vh',
      data: <options>{
        Columns: structuredClone(this.onColumnOptionsChange.value),
        DisplayColumns: this.columns
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      var r = <options>result;
      if (result && result.Columns) {
        this.onColumnOptionsChange.next(result.Columns);

        this.storageService.saveData(this.userId, this.getColumnOptionsStorageKey(), result.Columns);
      }
    });
  }

  private getFilterStorageKey() {
    return this.getStorageKey('filter');
  }

  private getSortStorageKey() {
    return this.getStorageKey('sort');
  }

  private getPageStorageKey() {
    return this.getStorageKey('page');
  }

  private getColumnOptionsStorageKey() {
    return this.getStorageKey('columnoptions');
  }

  private getStorageKey(suffix: string) {
    if (this.storageKeyIdentifier && this.enableStorage)
      return this.storageKeyIdentifier + '_' + suffix;

    return '';
  }

  private handleRowClicked(row: any) {
    if (!this.isRowClickable || this.isRowClickable(row)) {
      this.rowClicked.emit(row);
    }
  }

  private handleCellClicked(column: columnDef, row: any) {
    if (column?.ColumnSettings?.Clickable == true && !this.getLocked() && !row.editing && (!column.ColumnSettings.CanClick || column.ColumnSettings.CanClick(row))) {
      this.cellClicked.emit({
        column: column,
        row: row
      });
    }
  }

  private setElementEditing(element: any) {
    this.isAnyEditing = true;
    element.editing = true;
    element.editObject = {};

    for (var i = 0; i < this.columns.length; i++) {
      var col = this.columns[i];

      if (col.ColumnSettings?.Editable == true)
        element.editObject[col.Name] = element[col.Name];
    }
  }

  private deleteElement(element: any) {
    element.saving = true;
    this.saving = true;

    this.deleteRow.emit({
      element: element,
      complete: (success) => {
        if (success) {
          //Remove the element from the data
          const newData = { ...this.data.value };
          const index = newData.results.indexOf(element);
          if (index >= 0) {
            // Replace the array reference instead of mutating
            newData.results = [
              ...newData.results.slice(0, index),
              ...newData.results.slice(index + 1)
            ];
            this.data.next(newData);
          }

          this.saving = false;
        } else {
          element.saving = false;
          this.saving = false;
        }
      }
    });
  }

  private saveElementEditing(element: any) {
    element.saving = true;
    this.saving = true;

    this.saveRow.emit({
      element: element,
      complete: (success) => {
        if (success) {
          for (var i = 0; i < this.columns.length; i++) {
            var col = this.columns[i];

            if (col.ColumnSettings?.Editable == true)
              element[col.Name] = element.editObject[col.Name];
          }

          this.isAnyEditing = false;
          element.editing = false;
          element.editObject = null;
          element.saving = false;
          this.saving = false;
        } else {
          element.saving = false;
          this.saving = false;
        }
      }
    });
  }

  private saveAddItem() {
    this.addElementEditing(this.addingItem);
  }

  private addElementEditing(element: any) {
    element.saving = true;
    this.saving = true;

    this.addRow.emit({
      element: element,
      complete: (success) => {
        if (success) {
          for (var i = 0; i < this.columns.length; i++) {
            var col = this.columns[i];
            if (col.ColumnSettings?.Editable == true)
              element[col.Name] = element.editObject[col.Name];
          }
          this.addingItem = null;
          this.setAdding(false);
          element.saving = false;
          this.saving = false;
        } else {
          element.saving = false;
          this.saving = false;
        }
      }
    });
  }

  private cancelElementEditing(element: any) {
    element.editing = false;
    element.editObject = null;
    this.isAnyEditing = false;
    this.setAdding(false);
  }

  private getRowStyle(row: any) {
    if (this.rowStyle)
      return this.rowStyle(row);

    return '';
  }

  private handleEditKeyup(event: KeyboardEvent, element: any) {
    if (event.key === 'Enter' && !(event.shiftKey || event.ctrlKey || event.altKey)) {
      event.preventDefault();
      this.saveElementEditing(element);
    }
  }

  getType(variable: any): string {
    return typeof variable;
  }

  private clearSelected() {
    for (var i = 0; i < this.data.value.results.length; i++) {
      this.data.value.results[i].selected = false;
    }
  }

  private setDeleting(deleting: boolean) {
    this.isDeleting = deleting;
    this.clearSelected();
  }

  private setEditing(editing: boolean) {
    this.isEditing = editing;
    this.isAnyEditing = false;

    if (!this.isEditing)
      for (var i = 0; i < this.data.value.results.length; i++) {
        this.data.value.results[i].editing = false;
        this.data.value.results[i].editObject = null;
      }
  }

  private addingItem: any = null;
  private setAdding(adding: boolean) {
    this.isAdding = adding;
    if (!this.isAdding && this.addingItem != null) {
      const newData = { ...this.data.value };
      const index = newData.results.indexOf(this.addingItem);
      if (index >= 0) {
        // Replace the array reference instead of mutating
        newData.results = [
          ...newData.results.slice(0, index),
          ...newData.results.slice(index + 1)
        ];
        this.data.next(newData);
      }
      this.addingItem = null;
    } else if (this.isAdding && this.addingItem == null) {
      const newItem: any = {
        editing: true
      };
      for (const col of this.columns) {
        newItem[col.Name] = null;
      }
      newItem.editObject = {};

      for (var i = 0; i < this.columns.length; i++) {
        var col = this.columns[i];

        if (col.ColumnSettings?.Editable == true)
          newItem.editObject[col.Name] = newItem[col.Name];
      }
      this.addingItem = newItem;

      const newData = { ...this.data.value, results: [this.addingItem, ...this.data.value.results] };
      this.data.next(newData);
    }
  }

  private getLocked() {
    return this.locked || this.isEditing || this.isAdding || this.isDeleting || this.isAnyEditing || this.saving;
  }

  private cancel() {
    this.setAdding(false);

    //Loop through and cancel any elements editing
    for (var i = 0; i < this.data.value.results.length; i++) {
      var element = this.data.value.results[i];
      if (element.editing) {
        this.cancelElementEditing(element);
      }
    }

    this.isAdding = false;
    this.isEditing = false;
    this.isDeleting = false;
  }

  private deleteItems() {
    //Loop through and delete any elements selected
    var deletingItems = this.data.value.results.filter(item => item.selected && !item.deleting);

    this.saving = true;

    this.deleteRows.emit({
      elements: deletingItems,
      complete: (success) => {
        if (success) {
          this.saving = false;
          this.isDeleting = false;
          this.clearSelected();
        } else {
          this.saving = false;
        }
      }
    });
  }

  searchControls: { [key: string]: FormControl } = {};
  searchResults: { [key: string]: any[] } = {};
  searchLoading: { [key: string]: boolean } = {};
  private initSearchControl(rowIndex: string, columnName: string, searchFn: (term: string) => Observable<SearchValue[]>) {
    const key = `${rowIndex}_${columnName}`;
    if (this.searchControls[key]) return; // already initialized

    const control = new FormControl('');
    this.searchControls[key] = control;

    control.valueChanges
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        switchMap((term) => {
          if (term && term.length > 1) {
            this.searchLoading[key] = true;
            return searchFn(term).pipe(
              finalize(() => {
                this.searchLoading[key] = false;
              })
            );
          } else {
            this.searchLoading[key] = false;
            return of([]);
          }
        })
      )
      .subscribe((results) => {
        this.searchResults[key] = results;
      });
  }

  private onOptionSelected(rowIndex: string, columnName: string, element: any, column: columnDef, value: MatAutocompleteSelectedEvent) {
    console.log(`Selected for row ${rowIndex} column ${columnName}:`, value);
    element.editObject[columnName] = value.option.value;

    // Clear results
    this.searchResults[`${rowIndex}_${columnName}`] = [];
  }

  logInputDebug(rowId: string, columnName: string) {
    const key = `${rowId}_${columnName}`;
    console.log('Current value of control:', this.searchControls[key].value);
  }
}
